"use client";

import React from "react";
import { ArrowRight, ShieldCheck, Phone } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
export default function Action() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="call-to-action" data-nav="light" className="bg-background py-20 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-5 sm:px-8 max-w-6xl">
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.25
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative rounded-2xl md:rounded-3xl bg-secondary text-secondary-foreground p-8 sm:p-12 md:p-16 border border-border/20 shadow-2xl overflow-hidden">
          {/* Subtle architectural hairline overlay motif */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-10 bg-[radial-gradient(#C5A059_1px,transparent_1px)] [background-size:24px_24px]" />

          <div className="relative z-10 max-w-3xl mx-auto text-center flex flex-col items-center">
            {/* Eyebrow badge */}
            <Badge variant="outline" className="mb-6 rounded-full border-primary/40 bg-primary/10 px-4 py-1.5 text-xs font-semibold tracking-wider text-primary uppercase">
              Bespoke architectural craftsmanship
            </Badge>

            {/* Authoritative serif headline */}
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold leading-tight tracking-tight text-secondary-foreground mb-6">
              Bespoke renovation tailored to your home.
            </h2>

            {/* Concise supporting line */}
            <p className="text-base sm:text-lg text-secondary-foreground/80 leading-relaxed font-sans max-w-2xl mb-8">
              From custom kitchens to whole-home additions, we guide your project with dedicated
              planning, transparent estimates, and lasting craftsmanship across Brookhaven.
            </p>

            {/* Architectural brushed gold hairline accent */}
            <div className="w-full max-w-xs mb-8 flex items-center justify-center">
              <Separator className="bg-primary/40 h-px" />
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto flex-wrap">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground font-semibold px-8 py-3.5 rounded-lg shadow-md hover:bg-primary/90 transition-[background-color,box-shadow]">
                <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-2">
                  <span>Request an estimate</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border/40 text-secondary-foreground hover:bg-secondary-foreground/10 bg-transparent rounded-lg px-6 py-3.5 transition-[background-color,border-color]">
                <Link to="/OurProjectsPage">
                  <span>View our gallery</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border/40 text-secondary-foreground hover:bg-secondary-foreground/10 bg-transparent rounded-lg px-6 py-3.5 transition-[background-color,border-color]">
                <Link to="#">
                  <span>Explore our services</span>
                </Link>
              </Button>
            </div>

            {/* Direct phone consultation link & reassurance */}
            <div className="mt-8 pt-6 border-t border-border/15 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs sm:text-sm text-secondary-foreground/75 font-sans">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary" />
                <span>Detailed cost modeling</span>
              </div>
              <span className="hidden sm:inline opacity-40">•</span>
              <div className="flex items-center gap-2">
                <Phone className="size-3.5 text-primary" />
                <span>Call directly:</span>
                <a href="tel:+14708479076" className="font-medium text-secondary-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                  (470) 847-9076
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}